// Database Authentication Service
// API client for username/password authentication backend

import {
  DbUser,
  LoginRequest,
  LoginResponse,
  CreateUserRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  BulkUserCreateRequest,
  BulkUserCreateResponse,
} from '../types/dbAuth.types';
import { getBackendUrl as getRuntimeBackendUrl } from '../config/runtimeConfig';

// Use dedicated auth database URL (separate from chat storage)
/**
 * Get backend URL with priority:
 * 1. Runtime config (container startup injection - HIGHEST PRIORITY)
 * 2. Build-time env vars (development fallback)
 * 3. AdminContext localStorage (user override for testing only - LOWEST PRIORITY)
 */
function getBackendUrl(): string {
  // Priority 1 & 2: Runtime config (container env vars) > Build-time env vars
  const runtimeUrl = getRuntimeBackendUrl();
  if (runtimeUrl) {
    return runtimeUrl;
  }

  // Priority 3: AdminContext localStorage (fallback for testing/dev only)
  try {
    const adminConfigStr = localStorage.getItem('stellar_admin_config');
    if (adminConfigStr) {
      const adminConfig = JSON.parse(adminConfigStr);
      if (adminConfig.authDatabase?.backendUrl) {
        console.warn('[dbAuthService] ⚠️ Using backend URL from localStorage (low priority):', adminConfig.authDatabase.backendUrl);
        return adminConfig.authDatabase.backendUrl;
      }
    }
  } catch (error) {
    console.warn('[dbAuthService] Failed to load backend URL from admin config:', error);
  }

  // Final fallback
  return 'http://localhost:8000/api';
}

class DbAuthService {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('db_auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: 'Request failed' }));
      const errorMsg = error.error ||
        `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMsg);
    }
    return response.json();
  }

  /**
   * Login with username and password
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${getBackendUrl()}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });

    const data = await this.handleResponse<LoginResponse>(response);

    // Store token in localStorage
    localStorage.setItem('db_auth_token', data.token);

    return data;
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<DbUser> {
    const response = await fetch(`${getBackendUrl()}/auth/me`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<DbUser>(response);
  }

  /**
   * Logout (clear local token)
   */
  logout(): void {
    localStorage.removeItem('db_auth_token');
  }

  /**
   * Change current user's password
   */
  async changePassword(request: ChangePasswordRequest): Promise<void> {
    const response = await fetch(`${getBackendUrl()}/auth/change-password`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    await this.handleResponse<{ message: string }>(response);
  }

  /**
   * Verify if current token is valid
   */
  async verifyToken(): Promise<boolean> {
    const token = localStorage.getItem('db_auth_token');
    if (!token) return false;

    try {
      const response = await fetch(`${getBackendUrl()}/auth/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();
      return data.valid;
    } catch {
      return false;
    }
  }

  // User Management (Admin Only)

  /**
   * Get all users
   */
  async getAllUsers(): Promise<DbUser[]> {
    const response = await fetch(`${getBackendUrl()}/users`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<DbUser[]>(response);
  }

  /**
   * Get user by ID
   */
  async getUserById(id: number): Promise<DbUser> {
    const response = await fetch(`${getBackendUrl()}/users/${id}`, {
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<DbUser>(response);
  }

  /**
   * Create a new user
   */
  async createUser(request: CreateUserRequest): Promise<DbUser> {
    const response = await fetch(`${getBackendUrl()}/users`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);

        // Handle express-validator error format:
        // {"errors": [{msg: "...", path: "field"}]}
        if (errorJson.errors && Array.isArray(errorJson.errors)) {
          const errorMessages = errorJson.errors
            .map(
              (err: { path?: string; msg: string }) => {
                if (err.path) {
                  // Capitalize field name and make it clear this is a validation error
                  const fieldName = err.path.charAt(0).toUpperCase() + err.path.slice(1);
                  return `${fieldName} validation failed: ${err.msg}`;
                }
                return err.msg;
              }
            )
            .join('\n');
          throw new Error(errorMessages);
        }

        // Handle simple error format: {"error": "..."}
        if (errorJson.error) {
          throw new Error(errorJson.error);
        }

        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      } catch (e) {
        if (e instanceof Error) throw e;
        const errorMsg =
          `HTTP ${response.status}: ${errorText || response.statusText}`;
        throw new Error(errorMsg);
      }
    }

    return response.json();
  }

  /**
   * Create multiple users at once (bulk import)
   */
  async createBulkUsers(request: BulkUserCreateRequest): Promise<BulkUserCreateResponse> {
    const response = await fetch(`${getBackendUrl()}/users/bulk`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);

        // Handle express-validator error format
        if (errorJson.errors && Array.isArray(errorJson.errors)) {
          const errorMessages = errorJson.errors
            .map(
              (err: { path?: string; msg: string }) => {
                if (err.path) {
                  // Capitalize field name and make it clear this is a validation error
                  const fieldName = err.path.charAt(0).toUpperCase() + err.path.slice(1);
                  return `${fieldName} validation failed: ${err.msg}`;
                }
                return err.msg;
              }
            )
            .join('\n');
          throw new Error(errorMessages);
        }

        // Handle simple error format
        if (errorJson.error) {
          throw new Error(errorJson.error);
        }

        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      } catch (e) {
        if (e instanceof Error) throw e;
        const errorMsg =
          `HTTP ${response.status}: ${errorText || response.statusText}`;
        throw new Error(errorMsg);
      }
    }

    return response.json();
  }

  /**
   * Update user
   */
  async updateUser(id: number, request: UpdateUserRequest): Promise<DbUser> {
    const response = await fetch(`${getBackendUrl()}/users/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    return this.handleResponse<DbUser>(response);
  }

  /**
   * Delete user
   */
  async deleteUser(id: number): Promise<void> {
    const response = await fetch(`${getBackendUrl()}/users/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    await this.handleResponse<{ message: string }>(response);
  }

  /**
   * Check if token exists in localStorage
   */
  hasToken(): boolean {
    return !!localStorage.getItem('db_auth_token');
  }
}

export const dbAuthService = new DbAuthService();
